import dayjs from 'dayjs';
import { X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { InputGroupInput, InputGroupText } from '@/_shared/components/ui/input-group';
import { cn } from '@/_shared/lib/utils';

import { DateRangeValue } from './DateRangeValue';
import { DateValue } from './DateValue';
import type { FusedSelectOption } from './FusedSelect';
import { FusedSelect } from './FusedSelect';
import {
  asBoolean,
  asDate,
  asDateRange,
  asEnumMulti,
  asEnumMultiNumber,
  asEnumSingle,
  asNumber,
  asNumberRange,
  asText,
  asWindow,
} from './lib/operators';
import { RelativeWindowValue } from './RelativeWindowValue';
import type { AttributeType, ConditionValue, RowBounds, ValueShape } from './types';

const BOOLEAN_OPTIONS: FusedSelectOption[] = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
];

const FREE_INPUT_CLASS = '[field-sizing:content] max-w-72 min-w-24 flex-none';

/** A chrome-less number input for the InputGroup — empty string ⇄ null so clearing never commits 0. */
function NumberInput({
  value,
  onChange,
  ariaLabel,
  placeholder,
}: {
  value: number | null | undefined;
  onChange: (n: number | null) => void;
  ariaLabel: string;
  placeholder?: string;
}) {
  return (
    <InputGroupInput
      aria-label={ariaLabel}
      type="number"
      inputMode="numeric"
      placeholder={placeholder}
      value={value ?? ''}
      onChange={(e) => {
        const n = Number(e.target.value);
        onChange(e.target.value === '' || Number.isNaN(n) ? null : n);
      }}
      className="field-sizing-content min-w-14 flex-none px-2.5 text-center"
    />
  );
}

function SingleTextValue({
  value,
  onChange,
  ariaLabel,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  ariaLabel: string;
}) {
  return (
    <InputGroupInput
      aria-label={ariaLabel}
      type="text"
      placeholder="value…"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      className={FREE_INPUT_CLASS}
    />
  );
}

// Chip visuals mirror the combobox multi-select chips so a free-entry pill list belongs to the same family.
const PILL_CLASS =
  'flex h-6 w-fit max-w-full items-center gap-0.5 rounded-sm border border-grey-75 bg-grey-0 px-1.5 text-sm font-medium whitespace-nowrap text-grey-800';

const splitTokens = (s: string): string[] =>
  s
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

/** Free-entry value list, no popover — used for `is one of` / `is not one of` / `contains any of`. Committed
 *  values render as removable chips (matching the combobox multi-select); a comma or Enter trims and commits
 *  the token, Backspace on an empty field removes the last chip. There are no options to pick from. The
 *  container wraps and grows in height as pills accumulate (the parent InputGroup is bounded + `h-auto`). */
function MultiPillsValue<T extends string | number>({
  value,
  onChange,
  ariaLabel,
  parse,
}: {
  value: T[];
  onChange: (v: T[]) => void;
  ariaLabel: string;
  /** Turn a trimmed token into a committed value (identity for strings, `Number` for numeric columns); a
   *  `null` result drops the token (e.g. a non-numeric token on a number column). */
  parse: (token: string) => T | null;
}) {
  const [text, setText] = useState('');

  const commit = (raw: string) => {
    const next = [...value];
    for (const token of splitTokens(raw)) {
      const parsed = parse(token);
      if (parsed !== null && !next.includes(parsed)) next.push(parsed);
    }
    if (next.length !== value.length) onChange(next);
    setText('');
  };

  return (
    <div className="flex max-w-full min-w-0 flex-wrap items-center gap-0.5 px-px py-px">
      {value.map((v, i) => (
        <span key={i} className={PILL_CLASS}>
          {v}
          <Button
            variant="tertiary"
            size="sm"
            iconOnly
            aria-label={`Remove ${v}`}
            className="size-4 opacity-50 hover:opacity-100"
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <X className="pointer-events-none size-3" />
          </Button>
        </span>
      ))}
      <input
        aria-label={ariaLabel}
        type="text"
        placeholder={value.length ? '' : 'value 1, value 2…'}
        value={text}
        onChange={(e) => (e.target.value.includes(',') ? commit(e.target.value) : setText(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && text.trim()) {
            e.preventDefault();
            commit(text);
          } else if (e.key === 'Backspace' && text === '' && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => text.trim() && commit(text)}
        className={cn(
          'h-6 min-w-16 flex-1 bg-transparent text-sm leading-none font-medium text-grey-800 outline-none placeholder:font-normal placeholder:text-grey-300',
          // With no pills the input is the only child, so its placeholder would hug the box border. Pad the
          // left to match the pill's own px-1.5 so the empty placeholder lines up with where pill text sits.
          value.length === 0 && 'pl-1.5',
        )}
      />
    </div>
  );
}

/** A bound as a `YYYY-MM-DD` string, or undefined. `bounds` is typed `unknown` end-to-end because a
 *  number column's bounds are numbers; only a parseable date reaches a calendar. */
function asDateBound(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
}

/** Renders the value control(s) for a value-shape into the parent InputGroup, using design-system primitives.
 *  Every control is a plain input — `single_enum` is a text field, `multi_enum` a free-entry pill list; there
 *  are no observed-value pickers. `none` renders nothing. Used by AttributePredicateClause (the predicate value). */
export function ShapeValue({
  shape,
  value,
  onPatch,
  ariaBase,
  type,
  bounds,
}: {
  shape: ValueShape;
  value: ConditionValue;
  onPatch: (next: ConditionValue) => void;
  ariaBase: string;
  /** Column type — distinguishes numeric membership (`in`/`nin` on a number column) so multi_enum keeps `number[]`. */
  type?: AttributeType;
  /** The column's OBSERVED min/max. Used by the date controls only this iteration: a number column
   *  already gets its grounding from the segments control, and hinting a numeric input from bounds
   *  belongs with the missing-control-types work, not here. */
  bounds?: RowBounds;
}) {
  switch (shape) {
    case 'none':
      return null;
    case 'text':
      return (
        <InputGroupInput
          aria-label={`${ariaBase} value`}
          type="text"
          placeholder="text…"
          value={asText(value)}
          onChange={(e) => onPatch(e.target.value)}
          className="field-sizing-content max-w-72 min-w-24 flex-none"
        />
      );
    case 'single_enum':
      return (
        <SingleTextValue
          value={asEnumSingle(value)}
          onChange={(v) => onPatch(v ?? null)}
          ariaLabel={`${ariaBase} value`}
        />
      );
    case 'multi_enum':
      // A number column's `in`/`nin` must keep `number[]`; a string column keeps `string[]`.
      return type === 'number' ? (
        <MultiPillsValue
          value={asEnumMultiNumber(value)}
          onChange={onPatch}
          parse={(t) => {
            const n = Number(t);
            return Number.isFinite(n) ? n : null;
          }}
          ariaLabel={`${ariaBase} values`}
        />
      ) : (
        <MultiPillsValue
          value={asEnumMulti(value)}
          onChange={onPatch}
          parse={(t) => t}
          ariaLabel={`${ariaBase} values`}
        />
      );
    case 'number':
      return (
        <NumberInput
          value={asNumber(value)}
          onChange={(n) => onPatch(n)}
          ariaLabel={`${ariaBase} value`}
          placeholder="value"
        />
      );
    case 'number_range': {
      const [lo, hi] = asNumberRange(value);
      return (
        <>
          <NumberInput
            value={lo}
            onChange={(n) => onPatch([n, hi])}
            ariaLabel={`${ariaBase} lower bound`}
            placeholder="min"
          />
          <InputGroupText className="px-1">and</InputGroupText>
          <NumberInput
            value={hi}
            onChange={(n) => onPatch([lo, n])}
            ariaLabel={`${ariaBase} upper bound`}
            placeholder="max"
          />
        </>
      );
    }
    case 'boolean': {
      const b = asBoolean(value);
      return (
        <FusedSelect
          options={BOOLEAN_OPTIONS}
          value={b === undefined ? undefined : b ? 'true' : 'false'}
          onChange={(v) => onPatch(v === 'true')}
          ariaLabel={`${ariaBase} value`}
          placeholder="true / false"
        />
      );
    }
    case 'date':
      return (
        <DateValue
          value={asDate(value)}
          onChange={(d) => onPatch(d)}
          ariaLabel={`${ariaBase} date`}
          min={asDateBound(bounds?.min)}
          max={asDateBound(bounds?.max)}
        />
      );
    case 'date_range':
      return (
        <DateRangeValue
          value={asDateRange(value)}
          onChange={(r) => onPatch(r)}
          ariaLabel={`${ariaBase} date range`}
          min={asDateBound(bounds?.min)}
          max={asDateBound(bounds?.max)}
        />
      );
    case 'relative_window':
      return (
        <RelativeWindowValue value={asWindow(value)} onChange={(w) => onPatch(w)} ariaLabel={`${ariaBase} window`} />
      );
    default: {
      // Exhaustiveness guard — a newly-added ValueShape must handle a control here, not fall through to undefined.
      const _exhaustive: never = shape;
      return _exhaustive;
    }
  }
}
